import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { useTranslation } from "react-i18next";
import CustomModal from "../../../packages/CustomModal";
import WPDropdown from "../../../packages/WPDropdown";
import { CustomModalButtons } from "../../../constants/global.constants";
import { getColumnMapping } from "../../../network/cipAPI";
import createNotification, {
  ERROR_NOTIFICATION,
} from "../../../components/Notifications";
import { BANNER_TYPES } from "../../../packages/WPMessageBanner/WPMessageBanner";
import WPMessageBanner from "../../../packages/WPMessageBanner";

import "./importColumnMapping.scss";

const getInitialMapping = (t, columns) => {
  const initialMapping = Object.values(columns).map((col) => ({
    key: col.key,
    name: col.label || col.name,
    mappedTo: t("ValueStrings.fetching") + "...",
    options: col.options
      ? Object.values(col.options).map((op) => op.key)
      : undefined,
  }));

  return initialMapping;
};

const ImportColumnMapping = ({
  trains,
  columns,
  normalizedColumns,
  onImportColumnMapping,
  onModalClose,
}) => {
  const { t } = useTranslation();
  const [selectedTrain, setSelectedTrain] = useState(trains[0]);
  const [columnMapping, setColumnMapping] = useState(
    getInitialMapping(t, columns),
  );
  const [normalizationColumnMapping, setNormalizationColumnMapping] = useState(
    getInitialMapping(t, normalizedColumns),
  );
  const [loading, setLoading] = useState(false);

  const { file_id } = useSelector((state) => state);

  const trainOptions = trains.map((trainNum) => ({
    label: `${t("ValueStrings.train")} ${trainNum}`,
    value: trainNum,
  }));

  const handleModalClose = ({ target }) => {
    if (target.id === CustomModalButtons.CONFIRM) {
      onImportColumnMapping(columnMapping, normalizationColumnMapping);
      return;
    }
    onModalClose();
  };

  const mapColumnsWithResponse = (data, type = "columns") => {
    const columnMapping = getInitialMapping(
      t,
      type === "normalization" ? normalizedColumns : columns,
    );
    const updatedColumnMapping = columnMapping.map((col) => {
      const dataKeys = Object.keys(data);
      let key = col.key;
      let radioKey;

      if (col.options) {
        key = dataKeys.find((k) => col.options.includes(k));
        radioKey = col.key;
      }

      return {
        ...col,
        mappedTo: data[key],
        key,
        radioKey,
      };
    });

    return updatedColumnMapping;
  };

  const fetchColumnMappingForTrain = async (trainNum) => {
    setLoading(true);
    try {
      const response = await getColumnMapping(trainNum);
      if (response && response.data) {
        const data = response.data;

        const updatedColumnMapping = mapColumnsWithResponse(data);

        setColumnMapping(updatedColumnMapping);

        let updatedNormalizedColumn;
        if (data.norm_permeate_flow) {
          updatedNormalizedColumn = mapColumnsWithResponse(
            data,
            "normalization",
          );
        } else {
          updatedNormalizedColumn = [
            {
              name: "",
              mappedTo: t("analysisColumn.ftNormProLabel"),
              key: "normalization",
            },
          ];
        }
        setNormalizationColumnMapping(updatedNormalizedColumn);
      }
    } catch {
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
        "",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedTrain && file_id) {
      fetchColumnMappingForTrain(selectedTrain);
    }
  }, [selectedTrain, file_id]);

  return (
    <CustomModal
      header={t("columnSelection.importColumnMapping")}
      confirmBtn={t("columnSelection.importColumns")}
      cancelBtn={t("ValueStrings.cancel")}
      onModalClose={handleModalClose}
      customclass="import-column-mapping-modal"
      confirmBtnDisabled={!selectedTrain}
      isLoading={loading}
      width="850px"
    >
      <div className="import-column-mapping-content">
        <div className="train-selection-section">
          <span className="selection-label">
            {t("columnSelection.importColumnsFrom")}
          </span>
          <WPDropdown
            options={trainOptions}
            value={selectedTrain}
            onChange={(selectedOption) =>
              setSelectedTrain(selectedOption?.value)
            }
          />
        </div>
        <div className="column-summary-section">
          <div className="columns-flow">
            {columnMapping.map((mapping, index) => (
              <div key={index} className="column-item">
                <span className="column-name">{mapping.name}</span>
                <span className="column-tag">{mapping.mappedTo}</span>
              </div>
            ))}
            {normalizationColumnMapping.length > 0 && (
              <>
                <div className="line-divider"></div>
                <div
                  className={`normalization-title ${normalizationColumnMapping.length === 1 ? "fitContent" : ""}`}
                >
                  {t("analysisColumn.normalizationTitle")}
                </div>
                {normalizationColumnMapping.map((mapping, index) => (
                  <div key={index} className="column-item">
                    <span className="column-name">{mapping.name}</span>
                    <span className="column-tag">{mapping.mappedTo}</span>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
        <WPMessageBanner
          type={BANNER_TYPES.warning}
          message={t("columnSelection.importColumnMappingNote")}
          className="import-column-mapping-note"
        />
      </div>
    </CustomModal>
  );
};

export default ImportColumnMapping;
